// shortcutRegistry — レイアウトキーボードショートカットの **宣言的 single source
// of truth** (shortcut-settings.md §1, スライス1)。
//
// 由来 (2026-06-12): `useLayoutKeyboardShortcuts.ts` に `switch` 直書きされていた
// キーマップ (Ctrl/Cmd+Enter / V / Tab / P,B / M / E / +,= / - / 0) を 1 本の
// 宣言的レジストリに抽出する。配線フック (現スライス) に加え、後続スライスの
//   - スライス2: 一覧 read view (label / category / layouts を表描画)
//   - スライス3: リバインド UI (defaultKeys を override し effectiveKeymap を解決)
// が同じレジストリを参照する (= 配線・表示・リバインドの single source of truth)。
//
// ## 挙動完全維持 (スライス1 はリファクタ)
//
// 現 `switch` の attach 先 / preventDefault の有無を **逐語維持**するため、各 action に
// `scope` (素キー vs 修飾キー) から導出される既定挙動に加えて、`preventDefault` を
// action ごとに上書きできる field を持たせる。
//
// ## guard 特例の generic 化 (スライス3、2026-06-12)
//
// 旧 v1 では `chat.openPopup` (Ctrl/Cmd+Enter) / `view.flip` (素の V) を
// 専用 `guard` (`isOpenPopupHotkey` / `isCanvasFlipHotkey`) で発火させ固定行にしていた。
// スライス3 でこの 2 つを **他の rebindable action と同じ generic 経路に合流**させた:
//   - `chat.openPopup`: `defaultKeys: ['Ctrl+Enter', 'Meta+Enter']` で chord 2 本を
//     表現 (= 旧 `ctrlKey || metaKey` の cross-platform 挙動を維持)。chord なので
//     document attach + preventDefault。発火経路の IME ガードは generic 経路側で担保。
//   - `view.flip`: 素キー 'v' なので generic bare 経路 (window + `shouldSkipBareKey`)
//     に乗る (= 入力欄/修飾キーで発火しない)。preventDefault は false 維持。
// IME ガード (`!e.isComposing`) は generic 経路の chord/bare 両パスに追加した
// (= 全 action 共通の安全強化)。これにより専用 `guard` フィールドは不要になり撤去。
//
// ## scope と attach/ガードの導出 (shortcut-settings.md §4 D2)
//
// - `scope:'chord'` (修飾キー必須) → document attach。
// - `scope:'bare'`  (素キー)       → window attach + `shouldSkipBareKey` ガード。
//
// ## CSP流 循環ショートカット (docs/_wip/shortcut-settings.md「CSP の実モデル」2026-08-21 確定)
//
// CSP はショートカットを「個別ペン」「タブ」の各粒度に割り当てられ、**同じキーを割り
// 当てられた対象は押すたびに循環する**。よって「1 キー = 1 action」の前提を外し:
//   - `penRef`/`tabRef` (下記) で action を「個別ペンを選ぶ」「タブの最後に使ったペンを
//     選ぶ」に印付ける。印の無い action は従来どおりの「ただ発火するだけ」。
//   - **衝突検出の意味を反転**する: 旧 v1 は「同じキーは 1 つだけ」をエラーにしていたが
//     (steal-with-warn)、今は「同じキーへの複数割当 = 循環の宣言」であり正の仕様。
//     `detectConflict` (単一の衝突相手を返す・ブロック用) は撤去し、`findSameKeyActionIds`
//     (同じキーを持つ全 action を返す・情報提供用) に置き換えた。
//   - 実際の解決アルゴリズム (現在位置の導出・次 index の決定) は `shortcutCycle.ts`
//     (`resolveShortcutCycle`) を参照。ここ (registry) は「割当先の印付け」だけを担う。

import type { StrokePen } from '@oekaki/protocol';

import type { PenTabId } from '../../domain/PenTabs.js';

/** レイアウト識別子 (= レジストリの `layouts` で「この action を出す layout」を絞る)。 */
export type ShortcutLayout = 'classic' | 'modern';

/**
 * `LayoutKeyboardShortcutActions` の callback プロパティ名。registry の
 * `callbackKey` で action ↔ callback を解決するために使う (= 文字列ではなく
 * interface のキーに型で縛る)。
 */
export type ShortcutCallbackKey =
  | 'onOpenChatPopup'
  | 'onToggleUiHidden'
  | 'onSelectBrush'
  | 'onSelectSignpen'
  | 'onSelectEraserDirect'
  | 'onSelectEyedropper'
  // ── CSP流 循環ショートカット (2026-08-21): 個別ペンの直接選択 6 種 (brush/flat/eraser は
  // 上の既存 3 つのまま) + タブ (線画/塗り/質感) の「最後に使ったペンへ」3 種。────────
  | 'onSelectDot'
  | 'onSelectTone'
  | 'onSelectAirbrush'
  | 'onSelectCrayon'
  | 'onSelectPastel'
  | 'onSelectTexturePen'
  | 'onSelectTabLine'
  | 'onSelectTabFill'
  | 'onSelectTabTexture'
  | 'onZoomIn'
  | 'onZoomOut'
  | 'onZoomReset'
  | 'onZoomFit'
  | 'onToggleFlip'
  // キャンバス表示回転 (canvas-rotation 段取り 2): R = 回転ツール、左/右/リセットは既定キー無し。
  | 'onSelectRotateTool'
  | 'onRotateLeft'
  | 'onRotateRight'
  | 'onRotateReset'
  | 'onOpenShortcutSettings'
  | 'onCyclePreset'
  | 'onUndo'
  // `[`/`]`: 太さプリセット段階増減 (modern-layout.md §9.11 太さプリセット、
  // consult 2026-09-29、Modern 専用)。
  | 'onWidthStepDown'
  | 'onWidthStepUp';

/** listener を attach する DOM ターゲット。 */
export type ShortcutAttach = 'document' | 'window';

/**
 * 1 つのキーボードショートカット action の宣言。shortcut-settings.md §1 の
 * `ShortcutAction` に、挙動完全維持のための attach/preventDefault/guard を加えた形。
 */
export interface ShortcutAction {
  /** 安定 id (例 `'tool.brush'`)。永続化 override の key にもなる (スライス2)。 */
  readonly id: string;
  /**
   * 既定キー (素キー名 or chord 文字列)。複数既定を維持 (例 `['p','b']` / `['+','=']`)。
   * 各要素は `formatChord` / `parseChord` の chord 表記 (素キーは修飾無しの chord として
   * 扱える) で、override も同じ形で保存される。
   */
  readonly defaultKeys: readonly string[];
  /** 表示ラベル (i18n は後続、まず日本語文字列)。 */
  readonly label: string;
  /** 表示グループ (一覧 view のセクション)。 */
  readonly category: string;
  /** この action を出すレイアウト。 */
  readonly layouts: readonly ShortcutLayout[];
  /** 'chord'=修飾キー必須 / 'bare'=素キー。attach/ガードの既定を決める。 */
  readonly scope: 'bare' | 'chord';
  /** 呼び出す callback の名前 (`LayoutKeyboardShortcutActions` のキー)。 */
  readonly callbackKey: ShortcutCallbackKey;
  /**
   * listener attach 先の明示指定。省略時は scope から導出
   * (`chord`→document / `bare`→window)。`V` の特例 (`bare` だが document) 用。
   */
  readonly attach?: ShortcutAttach;
  /**
   * `preventDefault` を呼ぶか。省略時は scope から導出 (= 両 scope とも既定 true)。
   * `V` の特例 (= 素キーで browser 既定動作が無いので呼ばない) 用に `false` を明示。
   */
  readonly preventDefault?: boolean;
  /**
   * リバインド可能か (shortcut-settings.md §3、設計確定)。`true` の action だけ
   * 設定 UI でキャプチャ + per-row reset を出す。`false` (= 既定) の action は
   * read-only「固定」行として一覧に載せる:
   *   - `help.shortcuts` (`?`) は固定 (自己記述的に一覧へ載るが rebind 不可)。
   * (旧 v1 で固定だった `chat.openPopup` / `view.flip` はスライス3 で rebindable 化。)
   */
  readonly rebindable?: boolean;
  /**
   * `shouldSkipBareKey` の **Shift だけ許可**する緩和を行うか (= 既定 false)。
   * `?` (= Shift+/) のように **生成に Shift が要る bare key** 用。これらは Shift を
   * 除外すると永遠に発火しないため、chat 入力欄ガードは効かせつつ Shift は許す。
   * (Ctrl/Meta/Alt は引き続き除外。)
   */
  readonly allowShift?: boolean;
  /**
   * chord (scope='chord') であっても **入力欄フォーカス中は発火させない**か
   * (= 既定 false / 未指定)。既存の chord action (`chat.openPopup` 等) は
   * 「入力欄でも明示的な意図とみなして発火させる」が既定挙動 (document attach は
   * `shouldSkipBareKey` を通らない)。`edit.undo` (Ctrl/Cmd+Z) だけは例外: チャット
   * 入力欄でのテキスト編集 Undo (ブラウザ既定動作) を奪わないよう、`true` のときは
   * `shouldSkipBareKey` 相当の入力欄ガードを chord 経路にも適用する
   * (undo-conflict-aware-2026-07-12.md「テキスト入力フォーカス中は発火しない」)。
   */
  readonly guardTextInput?: boolean;
  /**
   * この action が「個別ペン `X` を選ぶ」ものであることの印 (CSP流 循環ショートカット、
   * 2026-08-21)。`resolveShortcutCycle` が「現在選択中の pen と一致する候補」を探す
   * ときに使う (= 仕様1〜4 の基準)。`tabRef` と同時に立てることは無い。消しゴムも
   * `penRef:'eraser'` として印付けられる (= タブに属さないが循環には参加する対象、
   * 2026-08-21 訂正)。
   */
  readonly penRef?: StrokePen;
  /**
   * この action が「タブ `X` の最後に使ったペンを選ぶ」ものであることの印。到達先の
   * 決定は呼び出し側 (`chrome.onSelectTabRef`) が行う — `resolveShortcutCycle` 自体は
   * `tabRef` を「現在位置の判定には使わない」ことだけ知っていればよい (= タブという
   * 状態は無く、選ばれた結果の pen だけが状態になる、shortcut-settings.md「仕様6」)。
   */
  readonly tabRef?: PenTabId;
}

// ── chord (修飾キー付きキー) の表記・パース・照合 (shortcut-settings.md §3 D2) ──
//
// override / defaultKeys の各要素は chord 文字列で表す。素キーは修飾無しの chord
// (= `'p'` / `'+'` / `'Tab'`) とみなす。修飾キー付きは `'Ctrl+m'` / `'Shift+/'` /
// `'Ctrl+Shift+e'` のように `+` 区切りで先頭に修飾キー (Ctrl/Meta/Alt/Shift 順) を置く。
//
// この chord 単位で attach 先を **動的に導出**する (= スライス2 申し送り):
//   - 修飾キー無し (bare) → window attach + shouldSkipBareKey ガード + preventDefault。
//   - 修飾キーあり (chord) → document attach + preventDefault。

/** chord をパースした構造体。`key` は base key (修飾を除いた 1 キー、小文字化前の生)。 */
export interface ParsedChord {
  readonly ctrl: boolean;
  readonly meta: boolean;
  readonly alt: boolean;
  readonly shift: boolean;
  /** base key (= 修飾を除いた末尾トークン)。空文字なら不正 chord。 */
  readonly key: string;
}

/**
 * chord 文字列 (`'Ctrl+Shift+e'` / `'p'` / `'+'`) を `ParsedChord` に分解する。
 * 末尾トークンを base key とし、それ以外の修飾トークンを大小無視で拾う。base key
 * 自体が `'+'` のケース (zoom in) があるので、`+` 区切りは「最後の `+` の前後」では
 * なく **トークン列の末尾を key、残りを修飾**として扱う。`'+'` 単体や `'Ctrl++'` を
 * 正しく解釈できるよう、末尾に空トークンが出たら base key を `'+'` に補正する。
 */
export const parseChord = (binding: string): ParsedChord => {
  const tokens = binding.split('+');
  let key = tokens[tokens.length - 1] ?? '';
  let modTokens = tokens.slice(0, -1);
  // `'+'` 単体 → tokens = ['','']、key='' → base key を '+' に補正 (zoom in 既定)。
  // `'Ctrl++'` → tokens = ['Ctrl','','']、key='' かつ直前が '' → base '+' + 修飾。
  if (key === '' && tokens.length >= 2) {
    key = '+';
    modTokens = tokens.slice(0, -2);
  }
  const lower = new Set(modTokens.map((t) => t.toLowerCase()));
  return {
    ctrl: lower.has('ctrl') || lower.has('control'),
    meta: lower.has('meta') || lower.has('cmd') || lower.has('command'),
    alt: lower.has('alt') || lower.has('option'),
    shift: lower.has('shift'),
    key,
  };
};

/** chord に修飾キーが 1 つでもあるか (= chord か bare かの判定)。 */
export const chordHasModifier = (c: ParsedChord): boolean => c.ctrl || c.meta || c.alt || c.shift;

/**
 * `KeyboardEvent` から chord 文字列を録音する (= キャプチャ UX 用)。修飾キーのみの
 * 押下 (Shift 等そのもの) は base key にできないので `null` を返す (= 録音継続)。
 * 並びは Ctrl → Meta → Alt → Shift → base key で正規化する。
 */
/**
 * `KeyboardEvent.key` を安全に取り出す (Sentry WEB-7)。
 *
 * spec 上 `key` は必ず string だが、**実際には undefined が来る**。本番で
 * `undefined is not an object (evaluating 't.key.toLowerCase')` が発生した
 * (WebKit の言い回し)。我々は合成キーイベントを一切 dispatch していないので、
 * アプリ内ブラウザ / パスワードマネージャ等の外部が `new Event('keydown')` のような
 * **`key` を持たないイベント**を投げているのが出所と考えられる。
 *
 * ここで弾かないと **keydown のたびに例外が飛び、以降のショートカットが全滅**する。
 * 型システムは `KeyboardEvent` を信じるので、実行時に確かめるしかない。
 */
export const eventKeyOf = (e: { readonly key?: unknown }): string | null =>
  typeof e.key === 'string' ? e.key : null;

export const formatChordFromEvent = (e: {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
}): string | null => {
  const k = eventKeyOf(e);
  // `key` を持たないイベント (WEB-7) は録音対象外。
  if (k === null) return null;
  // 修飾キー自体の単独押下は録音対象外 (= 次の本キーを待つ)。
  if (k === 'Control' || k === 'Meta' || k === 'Alt' || k === 'Shift') return null;
  const parts: string[] = [];
  if (e.ctrlKey) parts.push('Ctrl');
  if (e.metaKey) parts.push('Meta');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  // base key は Enter/Tab/矢印等はそのまま、英字は小文字化 (= 大小無視照合に揃える)。
  const base = k.length === 1 ? k.toLowerCase() : k;
  parts.push(base);
  return parts.join('+');
};

/** chord を表示用ラベルに整形する (例 `'Ctrl+m'` → `'Ctrl + M'`、`'+'` → `'+'`)。 */
export const formatChordLabel = (binding: string): string => {
  const c = parseChord(binding);
  const parts: string[] = [];
  if (c.ctrl) parts.push('Ctrl');
  if (c.meta) parts.push('Cmd');
  if (c.alt) parts.push('Alt');
  if (c.shift) parts.push('Shift');
  // base key: 1 文字英字は大文字表示、それ以外 (Tab/Enter/+/0 等) はそのまま。
  const base = c.key.length === 1 && /[a-z]/i.test(c.key) ? c.key.toUpperCase() : c.key;
  parts.push(base === 'Enter' ? 'Enter' : base);
  return parts.join(' + ');
};

/** binding 配列を「Tab」「P / B」のように人間可読へ整形する。空配列は「未割当」。 */
export const formatBindingsLabel = (bindings: readonly string[]): string =>
  bindings.length === 0 ? '未割当' : bindings.map(formatChordLabel).join(' / ');

/**
 * 1 つの chord binding が `KeyboardEvent` にマッチするか (= 録音した chord の照合)。
 * base key は大小無視 (= `matchKeySet` 流儀)。修飾キーは厳密一致を要求するが、
 * `allowExtraShift` のときだけ「binding が Shift 無しでも event の Shift を許す」
 * (= 素キー bare の従来挙動: `shouldSkipBareKey` が Shift を除外していたので bare 側は
 * そもそも Shift 無しのみ。chord 側は厳密)。bare/chord 判定は呼び出し側が attach で
 * 分離するので、ここでは binding の修飾と event の修飾を比較する。
 */
export const matchChordEvent = (binding: string, e: KeyboardEvent): boolean => {
  const c = parseChord(binding);
  if (c.key === '') return false;
  // WEB-7: `key` を持たないイベントが外部から飛んでくる。マッチしない扱いにする
  // (throw すると以降のショートカットが全滅する)。
  const eventKey = eventKeyOf(e);
  if (eventKey === null) return false;
  if (eventKey.toLowerCase() !== c.key.toLowerCase()) return false;
  return (
    e.ctrlKey === c.ctrl && e.metaKey === c.meta && e.altKey === c.alt && e.shiftKey === c.shift
  );
};

/**
 * 現キーマップのレジストリ。並びは「document attach (chord) → window attach (bare)」で
 * 旧コードの listener 構成に揃える。
 *
 * スライス3 (2026-06-12): `chat.openPopup` / `view.flip` の専用 guard を撤去し、
 * 他 action と同じ generic 経路 (chord→document / bare→window) に合流させた。
 * IME ガードは generic 経路側 (`useLayoutKeyboardShortcuts`) が全 action に効かせる。
 */
export const SHORTCUT_REGISTRY: readonly ShortcutAction[] = [
  // ── document attach (chord) ─────────────────────────────────────────────
  {
    id: 'chat.openPopup',
    // Ctrl+Enter / Meta+Enter の chord 2 本 (= 旧 `isOpenPopupHotkey` が許容していた
    // `ctrlKey || metaKey` の cross-platform 挙動を generic chord 経路で再現。Mac の
    // Cmd+Enter を壊さない)。設定画面では「Ctrl + Enter / Cmd + Enter」と表示される。
    // chord なので buildAttachPlan で document に振り分く + preventDefault。IME 変換中は
    // generic 経路の `!e.isComposing` ガードで発火しない (= 旧 isOpenPopupHotkey と等価)。
    defaultKeys: ['Ctrl+Enter', 'Meta+Enter'],
    label: '発言小窓を開く',
    category: 'チャット',
    layouts: ['classic', 'modern'],
    scope: 'chord',
    callbackKey: 'onOpenChatPopup',
    attach: 'document',
    preventDefault: true,
    rebindable: true,
  },
  {
    // conflict-aware Undo (docs/_wip/undo-conflict-aware-2026-07-12.md
    // 「Undo 拒否時の UI — 初期スコープに昇格」): Ctrl+Z / Cmd+Z で直近の cancel 可能な
    // 自分の stroke を取り消す (`useConflictAwareUndo.triggerUndo`)。chord (document
    // attach) だが `guardTextInput: true` — チャット入力欄でのテキスト編集 Undo
    // (ブラウザ既定動作) を奪わない。
    // 2026-07-23 是正 (modern-layout.md §1.1 表現パリティ違反の是正、
    // texture-brush-design.md §10.2 と同型): Undo はキャンバスに乗る表現操作なので
    // Classic/Modern 同時。ToolPanel は chrome 凍結方針のためボタンは新設せず、
    // 「到達可能性」= ショートカット (+ 設定画面での確認・リバインド) が Classic の
    // 受け皿。
    id: 'edit.undo',
    defaultKeys: ['Ctrl+z', 'Meta+z'],
    label: '元に戻す (Undo)',
    category: '編集',
    layouts: ['classic', 'modern'],
    scope: 'chord',
    callbackKey: 'onUndo',
    attach: 'document',
    preventDefault: true,
    guardTextInput: true,
    rebindable: true,
  },
  {
    id: 'view.flip',
    defaultKeys: ['v'],
    label: 'キャンバス左右反転',
    category: 'ビュー',
    layouts: ['classic', 'modern'],
    // 素キー 'v' なので generic bare 経路 (window + shouldSkipBareKey + IME ガード) に
    // 乗る (= 入力欄/修飾キー/IME 中は発火しない = 旧 isCanvasFlipHotkey と等価)。
    // 素キーは browser 既定動作が無いので preventDefault は呼ばない (false 維持)。
    scope: 'bare',
    callbackKey: 'onToggleFlip',
    preventDefault: false,
    rebindable: true,
  },
  {
    // 回転ツール (canvas-rotation 段取り 2、docs/_wip/canvas-rotation-2026-10-03.md): `R` で
    // スポイト (`I`) と同じ持続ツールへ切り替え、canvas ドラッグで回転 (中心は表示枠中央)。
    // ブラシ等を選ぶまで続く。Classic / Modern 両方で効く (ボタンは Modern のみ・段取り 3)。
    id: 'tool.rotate',
    defaultKeys: ['r'],
    label: '回転ツール',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectRotateTool',
    rebindable: true,
  },
  {
    // 左回転 / 右回転 / 回転リセットは **既定キー無し** (リバインド UI で割り当てる)。
    // CSP の `-` / `^` は採用しない (`-` はズームアウトと衝突、`^` は US 配列で押しにくい)。
    // 回転軸は表示枠の中央、1 回のステップは `ROTATION_STEP_DEG`。
    id: 'view.rotateLeft',
    defaultKeys: [],
    label: 'キャンバス左回転',
    category: 'ビュー',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onRotateLeft',
    rebindable: true,
  },
  {
    id: 'view.rotateRight',
    defaultKeys: [],
    label: 'キャンバス右回転',
    category: 'ビュー',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onRotateRight',
    rebindable: true,
  },
  {
    id: 'view.rotateReset',
    defaultKeys: [],
    label: 'キャンバス回転リセット',
    category: 'ビュー',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onRotateReset',
    rebindable: true,
  },
  // ── window attach (素キー, shouldSkipBareKey ガード) ─────────────────────
  {
    id: 'ui.toggleHidden',
    defaultKeys: ['Tab'],
    label: 'UI 表示/非表示',
    category: 'ビュー',
    // 2026-06-12 (shortcut-settings.md「Modern Tab 決着」): Modern でも Tab を解禁。
    // Classic は UI 全体隠し、Modern は両サイドバー (左右 <aside>) 非表示 = canvas
    // 最大化、と挙動は layout で別物だが、registry action は共通 1 本 (= 設定画面で
    // Modern でも有効/リバインド可)。
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onToggleUiHidden',
    rebindable: true,
  },
  {
    // §9.10 決定4 (consult 2026-06-16): ワークスペース preset を巡回する。Tab
    // (= ui.toggleHidden、focus トグル) は据え置き・触らない。preset cycle は N 状態ナビ
    // なので別アクションに分離する (= Tab に N 状態を乗せると muscle memory を壊す)。
    // 既定キーは `` ` `` (backquote): Tab と衝突せず、英字ツールキー (B/S/E/I) とも被らず、
    // 入力欄では shouldSkipBareKey で無効化される素キー。rebindable なので「Tab を cycle に
    // したい」上級者は設定で自分で変えられる (= 既定は gateway 安全側、§9.3 二層モデル)。
    // Modern 限定 (Classic に preset 概念は無い → callback 未渡し = no-op)。
    id: 'layout.cycle-preset',
    defaultKeys: ['`'],
    label: 'ワークスペースを切替',
    category: 'ビュー',
    layouts: ['modern'],
    scope: 'bare',
    callbackKey: 'onCyclePreset',
    rebindable: true,
  },
  {
    // 1キー1ツール (2026-06-12): P (ブラシ別名) を廃止し B のみに統一。
    // `penRef:'brush'` (2026-08-21): CSP流循環の「線画」タブ先頭メンバー。
    id: 'tool.brush',
    defaultKeys: ['b'],
    label: 'ブラシ',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectBrush',
    penRef: 'brush',
    rebindable: true,
  },
  {
    // marker (コピック) 段階撤去に伴い M (マーカー bucket toggle) を撤去し、
    // S をサインペン (`onSelectPen('flat')`) 直接選択キーとして新設 (二層モデル
    // Tier 1、modern-layout.md §9.3 / shortcut-settings.md スライス3)。
    // `penRef:'flat'` (2026-08-21): CSP流循環の「線画」タブメンバー。
    id: 'tool.signpen',
    defaultKeys: ['s'],
    label: 'サインペン',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectSignpen',
    penRef: 'flat',
    rebindable: true,
  },
  // ── CSP流 循環ショートカット (2026-08-21、shortcut-settings.md「CSP の実モデル」確定) ──
  // 個別ペンの直接選択 (既定キー未割当、リバインド可) + タブ (線画/塗り/質感) の
  // 「最後に使ったペンへ」(既定キー未割当)。**この並び順が循環順序そのもの**
  // (= 設定画面の並び順、`domain/PenTabs.ts` の PEN_TABS と同じタブ×タブ内順で並べる)。
  // 既定キーマップでは各アクションに 1 件も割り当たらないため、既定挙動では循環は
  // 発火しない (= B=ブラシ/S=サインペン/E=消しゴム の従来挙動を維持)。
  {
    id: 'tool.pen.dot',
    defaultKeys: [],
    label: 'ドット',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectDot',
    penRef: 'dot',
    rebindable: true,
  },
  {
    id: 'tool.pen.tone',
    defaultKeys: [],
    label: 'トーン',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectTone',
    penRef: 'tone',
    rebindable: true,
  },
  {
    // 「線画」タブの最後に使ったペンを選ぶ (= brush/flat/dot/tone のどれか)。
    // 既定キー `B` は `tool.brush` と共有 (2026-08-21、shortcut-settings.md 案 C)。
    // 同キー = 循環の宣言なので、`B` 連打で ブラシ ⇄ 線画タブで最後に使ったペン を巡る。
    id: 'tool.tab.line',
    defaultKeys: ['b'],
    label: '線画タブ',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectTabLine',
    tabRef: 'line',
    rebindable: true,
  },
  {
    id: 'tool.pen.airbrush',
    defaultKeys: [],
    label: 'エアブラシ',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectAirbrush',
    penRef: 'airbrush',
    rebindable: true,
  },
  {
    // 「塗り」タブの最後に使ったペンを選ぶ (現状は airbrush のみ)。
    // 既定キー `G` (空きキー。Photoshop の塗り系 G の連想)。
    id: 'tool.tab.fill',
    defaultKeys: ['g'],
    label: '塗りタブ',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectTabFill',
    tabRef: 'fill',
    rebindable: true,
  },
  {
    id: 'tool.pen.texture',
    defaultKeys: [],
    label: 'テクスチャ',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectTexturePen',
    penRef: 'texture',
    rebindable: true,
  },
  {
    id: 'tool.pen.crayon',
    defaultKeys: [],
    label: 'クレヨン',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectCrayon',
    penRef: 'crayon',
    rebindable: true,
  },
  {
    id: 'tool.pen.pastel',
    defaultKeys: [],
    label: 'パステル',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectPastel',
    penRef: 'pastel',
    rebindable: true,
  },
  {
    // 「質感」タブの最後に使ったペンを選ぶ (= texture/crayon/pastel のどれか)。
    // 既定キー `T` (空きキー。テクスチャの頭文字)。
    id: 'tool.tab.texture',
    defaultKeys: ['t'],
    label: '質感タブ',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectTabTexture',
    tabRef: 'texture',
    rebindable: true,
  },
  {
    // `penRef:'eraser'` (2026-08-21): 消しゴムはどのタブにも属さないが、循環には
    // タブに属さないフラットな対象として参加できる (訂正、上部コメント参照)。
    id: 'tool.eraser',
    defaultKeys: ['e'],
    label: '消しゴム',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onSelectEraserDirect',
    penRef: 'eraser',
    rebindable: true,
  },
  {
    // スポイト持続モード (§9.6 方針転換 2026-06-12)。Modern ツール帯のボタンと同じ
    // 持続トグルを `I` で叩く (= Photoshop/CSP の I 慣習)。Classic はボタンを足さず
    // ジェスチャのみなので **modern 限定** (callback 未渡し = Classic では no-op)。
    // 別行の read-only `gesture.altEyedropper` (Alt+クリック) はそのまま残す (両立)。
    id: 'tool.eyedropper',
    defaultKeys: ['i'],
    label: 'スポイト',
    category: 'ツール',
    layouts: ['modern'],
    scope: 'bare',
    callbackKey: 'onSelectEyedropper',
    rebindable: true,
  },
  {
    // `[`: 太さプリセット段階減 (modern-layout.md §9.11、consult 2026-09-29)。
    // 「小さいうちは 1px、大きくなると粗く」段階的に刻む (`widthPresets.ts`
    // `stepPenWidth`)。プリセット登録リストとは別軸 (= 登録済みボタンをクリックする
    // のではなく、現在値から段階的に増減する)。2026-10-01 に Classic にも拡張: Classic
    // ではスライダを 1 段動かしたのと同じ扱い (= 選択中 cell の値も書き換わる、旧版
    // 踏襲の上書き型)。
    id: 'tool.width.decrease',
    defaultKeys: ['['],
    label: '太さを細く',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onWidthStepDown',
    rebindable: true,
  },
  {
    // `]`: 太さプリセット段階増 (上と対称)。
    id: 'tool.width.increase',
    defaultKeys: [']'],
    label: '太さを太く',
    category: 'ツール',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onWidthStepUp',
    rebindable: true,
  },
  {
    id: 'zoom.in',
    defaultKeys: ['+', '='],
    label: 'ズーム in',
    category: 'ズーム',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onZoomIn',
    rebindable: true,
  },
  {
    id: 'zoom.out',
    defaultKeys: ['-'],
    label: 'ズーム out',
    category: 'ズーム',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onZoomOut',
    rebindable: true,
  },
  {
    id: 'zoom.reset',
    defaultKeys: ['0'],
    label: 'ズームリセット',
    category: 'ズーム',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onZoomReset',
    rebindable: true,
  },
  {
    // 全体表示 (フィット、決定3 §6 consult 2026-09-29): canvas 全景が表示スロットに
    // 収まる zoom へ 1 手で戻す (`chrome.applyFitZoom` = M1-a の入室時フィットと同じ
    // 計算)。既定キーは空きキー `f` (fit の頭文字。CSP/PS 慣習の Ctrl+0 は素の `0`
    // = 等倍リセットと衝突するため避けた、実装時提案)。ボタンは desktop Modern の
    // ツール帯のみ (Classic は chrome 凍結方針、ショートカットのみで到達)。
    id: 'zoom.fit',
    defaultKeys: ['f'],
    label: '全体表示 (フィット)',
    category: 'ズーム',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onZoomFit',
    rebindable: true,
  },
  {
    // `?` (= Shift+/) でショートカット設定モーダルを開く (shortcut-settings.md §5)。
    // 自己記述的に一覧に載るが v1 では固定 (rebindable 無し)。Shift が要るので
    // allowShift で bare ガードの Shift 除外を緩和する (= chat 入力中は無効のまま)。
    id: 'help.shortcuts',
    defaultKeys: ['?'],
    label: 'ショートカット設定を開く',
    category: 'ヘルプ',
    layouts: ['classic', 'modern'],
    scope: 'bare',
    callbackKey: 'onOpenShortcutSettings',
    preventDefault: true,
    allowShift: true,
  },
];

/**
 * v1 リバインド対象外の **ジェスチャ系操作** (= 別フック / engine 側、`useLayoutKeyboardShortcuts`
 * の管轄外)。一覧に read-only「固定」行として説明テキストだけ載せるための表示用宣言
 * (shortcut-settings.md §3)。`SHORTCUT_REGISTRY` とは別配列で、配線には一切使わない。
 */
export interface GestureShortcutEntry {
  readonly id: string;
  /** 表示するキー / 操作 (例 `'Space (長押し)'`)。 */
  readonly keysLabel: string;
  readonly label: string;
  readonly category: string;
  readonly layouts: readonly ShortcutLayout[];
}

export const GESTURE_SHORTCUTS: readonly GestureShortcutEntry[] = [
  {
    id: 'gesture.spacePan',
    keysLabel: 'Space (長押し) + ドラッグ',
    label: 'キャンバスを掴んで移動 (パン)',
    category: 'ジェスチャ',
    layouts: ['classic', 'modern'],
  },
  {
    id: 'gesture.shiftSpaceRotate',
    keysLabel: 'Shift + Space (長押し) + ドラッグ',
    label: 'キャンバスを回転 (回転ツール R の一時切り替え)',
    category: 'ジェスチャ',
    layouts: ['classic', 'modern'],
  },
  {
    id: 'gesture.altEyedropper',
    keysLabel: 'Alt + クリック',
    label: 'スポイト (色を吸い取る)',
    category: 'ジェスチャ',
    layouts: ['classic', 'modern'],
  },
  {
    id: 'gesture.wheelZoom',
    keysLabel: 'ホイール',
    label: 'カーソル位置を中心にズーム',
    category: 'ジェスチャ',
    layouts: ['classic', 'modern'],
  },
];

/**
 * action ごとの実効キーマップ (= `defaults ⊕ userOverrides` を解決する純関数、
 * shortcut-settings.md §3 スライス2)。override に当該 action の entry があれば
 * **それで完全に置き換える** (= merge ではなく置換。`[]` なら未割当)。entry が
 * 無ければ `defaultKeys` をそのまま返す (= 差分のみ保存・既定変更が自動で流れる)。
 *
 * @param action 対象 action
 * @param overrides per-user override 差分 (省略時は override 無し = 既定)。
 * @returns 実効キー配列。
 */
export const resolveEffectiveKeys = (
  action: ShortcutAction,
  overrides?: Readonly<Record<string, readonly string[]>>,
): readonly string[] => {
  const override = overrides?.[action.id];
  return override ?? action.defaultKeys;
};

/**
 * 同じ実効キーを持つ他 action の id 一覧を返す (CSP流 循環ショートカット、
 * shortcut-settings.md「CSP の実モデル」2026-08-21 確定・純関数)。旧 v1 の
 * `detectConflict` は「1 件だけ返してブロックする」steal-with-warn 用だったが、
 * **同じキーへの複数割当は正の仕様 (= 循環の宣言) になった**ため、ブロックせず
 * **該当する全 action の id を情報提供として返す**形に置き換えた。呼び出し側
 * (`ShortcutSettingsPanel`) はこれを「押すたびに循環する相手」の案内表示に使う。
 *
 * 比較は chord 正規化 (Ctrl/Meta/Alt/Shift 順 + base key 小文字) で行うので、表記揺れ
 * (`'Shift+a'` vs `'a+Shift'`) でも同一視できる。固定行 (rebindable でない action) も
 * 対象に含める (= 例えば Ctrl+Enter と同じキーの割当も検出する)。
 *
 * @returns 同じキーを持つ他 action の id 配列 (登録順)。無ければ空配列。自分自身
 *   (`targetId`) は除外。
 */
export const findSameKeyActionIds = (
  candidate: string,
  targetId: string,
  registry: readonly ShortcutAction[],
  overrides?: Readonly<Record<string, readonly string[]>>,
): readonly string[] => {
  const norm = (b: string): string => {
    const c = parseChord(b);
    return JSON.stringify([c.ctrl, c.meta, c.alt, c.shift, c.key.toLowerCase()]);
  };
  const target = norm(candidate);
  const ids: string[] = [];
  for (const action of registry) {
    if (action.id === targetId) continue;
    const keys = resolveEffectiveKeys(action, overrides);
    if (keys.some((k) => norm(k) === target)) ids.push(action.id);
  }
  return ids;
};

/**
 * `attach` を明示しなければ scope から導出する (chord→document / bare→window)。
 */
export const resolveAttach = (action: ShortcutAction): ShortcutAttach =>
  action.attach ?? (action.scope === 'chord' ? 'document' : 'window');

/**
 * `preventDefault` を明示しなければ既定 true (= 旧 switch は全 case で呼んでいた)。
 */
export const resolvePreventDefault = (action: ShortcutAction): boolean =>
  action.preventDefault ?? true;

/**
 * 素キー (defaultKeys マッチ) 用の照合キーセット。大小無視で比較するため
 * 全て小文字化した Set を返す (旧 switch の `case 'p': case 'P':` 等を一般化)。
 */
export const matchKeySet = (keys: readonly string[]): ReadonlySet<string> =>
  new Set(keys.map((k) => k.toLowerCase()));
